import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Maximize2, BedDouble, ArrowUpRight, ArrowRight, Images } from "lucide-react";
import { WhatsAppIcon } from "./Logo";
import { ShareIconButton } from "./ShareButton";
import { getListings, resolveImage, logEnquiry } from "../lib/api";
import { waLink } from "../lib/brand";

const dealTone = {
  Sale: "bg-[#131311] text-[#faf3e0]",
  Purchase: "bg-[#9a7b38] text-white",
  Rent: "bg-[#25d366] text-white",
};

export const ListingCard = ({ l }) => {
  const gallery = l.images && l.images.length ? l.images : (l.image_url ? [l.image_url] : []);
  const to = `/p/${l.slug || l.id}`;
  return (
  <article
    className="group w-[300px] sm:w-[340px] shrink-0 overflow-hidden rounded-3xl bg-[#ffffff] border border-[#e7e3d7] hover:border-[#c5a059] transition-colors duration-500"
    data-testid={`listing-card-${l.id}`}
  >
    <Link to={to} className="block relative h-48 overflow-hidden" data-testid={`listing-image-link-${l.id}`}>
      <img
        src={resolveImage(l.image_url || gallery[0])}
        alt={l.title}
        className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#131311]/45 via-transparent to-transparent" />
      <span className={`absolute top-3 left-3 rounded-full px-3 py-1 text-[11px] font-semibold ${dealTone[l.deal_type] || "bg-[#131311] text-white"}`}>
        For {l.deal_type}
      </span>
      {gallery.length > 1 && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white">
          <Images className="h-3.5 w-3.5" /> {gallery.length}
        </span>
      )}
      {l.price && (
        <span className="absolute bottom-3 left-3 font-serif text-xl text-[#faf3e0] drop-shadow">
          {l.price}
        </span>
      )}
    </Link>
    <div className="p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#9a7b38] font-semibold">
        {l.category}
      </div>
      <Link to={to} className="block" data-testid={`listing-title-link-${l.id}`}>
        <h3 className="mt-1 font-serif text-xl font-semibold text-[#131311] leading-tight line-clamp-2 group-hover:text-[#9a7b38] transition-colors">
          {l.title}
        </h3>
      </Link>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6b675c]">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" /> {l.location}
        </span>
        {l.area && (
          <span className="inline-flex items-center gap-1">
            <Maximize2 className="h-3.5 w-3.5" /> {l.area}
          </span>
        )}
        {l.bedrooms && (
          <span className="inline-flex items-center gap-1">
            <BedDouble className="h-3.5 w-3.5" /> {l.bedrooms}
          </span>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Link
          to={to}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#131311] px-4 py-2.5 text-sm font-semibold text-[#faf9f5] hover:bg-[#9a7b38] transition-all duration-300"
          data-testid={`listing-view-details-${l.id}`}
        >
          View details <ArrowUpRight className="h-4 w-4" />
        </Link>
        <a
          href={waLink(`Hello Laxmi Jiwan Properties, I'm interested in "${l.title}" (${l.location}). Please share more details.`)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logEnquiry({ listing_id: l.id, title: l.title, location: l.location, source: "listing" })}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#131311] p-2.5 text-sm font-semibold text-[#131311] hover:bg-[#25d366] hover:border-[#25d366] hover:text-white transition-all duration-300"
          data-testid={`listing-enquire-${l.id}`}
          aria-label="Enquire on WhatsApp"
        >
          <WhatsAppIcon className="h-4 w-4" />
        </a>
        <ShareIconButton slug={l.slug || l.id} title={l.title} className="p-2.5" />
      </div>
    </div>
  </article>
  );
};

export const Listings = () => {
  const [listings, setListings] = useState([]);

  useEffect(() => {
    getListings(true).then(setListings).catch(() => setListings([]));
  }, []);

  if (listings.length === 0) return null;
  const track = listings.length < 4 ? [...listings, ...listings, ...listings] : [...listings, ...listings];

  return (
    <section id="listings" className="relative py-16 sm:py-24 lg:py-32 bg-[#f4efdf] overflow-hidden" data-testid="listings-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 mb-8 sm:mb-12">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-10 bg-[#c5a059]" />
          <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
            Featured Properties
          </span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#131311] leading-[1]">
            Handpicked properties <span className="italic text-gold-gradient">in Hisar.</span>
          </h2>
          <a
            href="/properties"
            className="inline-flex items-center gap-2 rounded-full bg-[#131311] text-[#faf9f5] px-6 py-3 text-sm font-semibold hover:bg-[#9a7b38] transition-colors duration-300 shrink-0"
            data-testid="view-all-properties"
          >
            View all properties <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Mobile: swipeable scroller */}
      <div
        className="sm:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory px-4 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-testid="listings-scroller-mobile"
      >
        {listings.map((l) => (
          <div key={l.id} className="snap-start shrink-0">
            <ListingCard l={l} />
          </div>
        ))}
      </div>

      {/* Desktop: auto-marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative hidden sm:block"
      >
        <div className="lj-cards-track px-4 sm:px-8 lg:px-12">
          {track.map((l, i) => (
            <ListingCard key={`${l.id}-${i}`} l={l} />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-[#f4efdf] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-[#f4efdf] to-transparent" />
      </motion.div>
    </section>
  );
};
